import apiService from './apiService';
import { getApiBaseUrl } from '../config/apiConfig';

const PREFIX = '/klub/chiffrage';

async function request(promise) {
  const res = await promise;
  if (!res.success) {
    throw new Error(res.message || 'Erreur API');
  }
  return res;
}

async function fetchExportBlob(url, defaultFilename) {
  const token = localStorage.getItem('auth_token') || localStorage.getItem('jwt_token');
  const response = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}`, Accept: '*/*' } : { Accept: '*/*' },
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || `Erreur export (HTTP ${response.status})`);
  }

  const rawBlob = await response.blob();
  const blob = rawBlob.type === 'application/pdf'
    ? rawBlob
    : new Blob([rawBlob], { type: 'application/pdf' });
  const disposition = response.headers.get('Content-Disposition') || '';
  const match = disposition.match(/filename="?([^";]+)"?/);
  const filename = match?.[1] || defaultFilename;

  return { blob, filename };
}

async function downloadExport(url, defaultFilename) {
  const { blob, filename } = await fetchExportBlob(url, defaultFilename);
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  URL.revokeObjectURL(link.href);
}

function buildBdcPdfUrl(id, options = {}) {
  const base = getApiBaseUrl();
  const params = new URLSearchParams();
  Object.entries(options).forEach(([key, value]) => {
    if (value != null && String(value).trim() !== '') {
      params.set(key, value);
    }
  });
  const query = params.toString();
  return `${base}${PREFIX}/dossiers/${id}/bdc/pdf${query ? `?${query}` : ''}`;
}

function bdcPdfFilename(options = {}) {
  return options.document_titre === 'DEVIS' ? 'DEVIS.pdf' : 'BDC_complet.pdf';
}

const klubChiffrageService = {
  async getMeta() {
    const res = await request(apiService.get(`${PREFIX}/meta`));
    return res.data;
  },

  async getSession() {
    const res = await request(apiService.get(`${PREFIX}/session`));
    return res.data;
  },

  async startNewSession() {
    const res = await request(apiService.post(`${PREFIX}/session/new`));
    return res.data;
  },

  async openDossier(id) {
    const res = await request(apiService.post(`${PREFIX}/dossiers/${id}/open`));
    return res.data;
  },

  async duplicateDossier(id) {
    const res = await request(apiService.post(`${PREFIX}/dossiers/${id}/duplicate`));
    return res.data;
  },

  async listDossiers({ search = '', page = 1, limit = 20, scope = 'mine', commercial_id = '' } = {}) {
    const res = await request(apiService.get(`${PREFIX}/dossiers`, {
      search,
      page,
      limit,
      scope,
      commercial_id: commercial_id || undefined,
    }));
    return { dossiers: res.data.dossiers ?? [], pagination: res.pagination ?? {} };
  },

  async updateDossier(id, payload) {
    const res = await request(apiService.put(`${PREFIX}/dossiers/${id}`, payload));
    return res.data;
  },

  async deleteDossier(id) {
    const res = await request(apiService.delete(`${PREFIX}/dossiers/${id}`));
    return res.data;
  },

  async resetDossier(id) {
    const res = await request(apiService.post(`${PREFIX}/dossiers/${id}/reset`));
    return res.data;
  },

  async prepareBdc(id) {
    const res = await request(apiService.post(`${PREFIX}/dossiers/${id}/bdc/prepare`));
    return res.data;
  },

  async pushDolibarr(id, bdcOptions = {}) {
    const res = await request(apiService.post(`${PREFIX}/dossiers/${id}/push-dolibarr`, {
      bdc_options: bdcOptions,
    }));
    return res.data;
  },

  fetchBdcPdf(id, options = {}) {
    return fetchExportBlob(buildBdcPdfUrl(id, options), bdcPdfFilename(options));
  },

  downloadBdcPdf(id, options = {}) {
    return downloadExport(buildBdcPdfUrl(id, options), bdcPdfFilename(options));
  },
};

export default klubChiffrageService;
